<template>
  <Menu
    v-slot="{ open }"
    as="div"
    class="relative"
    @mouseenter="isMouseWithin = true"
    @mouseleave="isMouseWithin = false"
  >
    <MenuButton as="div" class="flex w-full items-center" ref="menu-button">
      <slot :open="open">
        <BaseIconButton icon="more_vert" :force-pressed="open" />
      </slot>
    </MenuButton>
    <Teleport to="#menu-container" defer>
      <div
        ref="floatingEl"
        v-show="open || (displayAsChild && isMouseWithin)"
        class="fixed z-100 my-1 focus:outline-none"
        :style="floatingStyles"
      >
        <MenuItems :unmount="false" :static="true">
          <div v-if="isGrouped" class="flex flex-col gap-0.5">
            <div
              v-for="(group, index) in groupedItems"
              :key="index"
              class="bg-surface-container first:rounded-t-large last:rounded-b-large rounded-small shadow-2 [&>button]:rounded-extra-small! first:[&>button:first-child]:rounded-t-large! last:[&>button:last-child]:rounded-b-large! min-w-36 px-1 py-1"
            >
              <div v-if="group.label" class="text-label-large text-on-surface-variant px-3 py-2">
                {{ group.label }}
              </div>
              <MenuItem
                v-for="item in group.items"
                :key="item.id"
                :item="item"
                :has-leading-icons="hasLeadingIcons"
                :has-item-with-trailing-content="hasItemWithTrailingContent"
                :prevent-close="multiSelect"
              />
            </div>
          </div>
          <div v-else class="bg-surface-container rounded-large shadow-2 min-w-36 px-1 py-1">
            <MenuItem
              v-for="item in ungroupedItems"
              :key="item.id"
              :item="item"
              :has-leading-icons="hasLeadingIcons"
              :has-item-with-trailing-content="hasItemWithTrailingContent"
              :prevent-close="multiSelect"
            />
          </div>
        </MenuItems>
      </div>
    </Teleport>
  </Menu>
</template>
<script setup lang="ts">
  import { Menu, MenuButton, MenuItems } from '@headlessui/vue'
  import { type MenuItemGroup, type MenuItemType, type SingleMenuItem } from './MenuItem'
  import { autoUpdate, flip, useFloating } from '@floating-ui/vue'
  import { computed, ref, useTemplateRef } from 'vue'
  import BaseIconButton from '../button/icon-button/BaseIconButton.vue'
  import MenuItem from './MenuItem.vue'

  interface Props {
    items: MenuItemType
    displayAsChild?: boolean
    multiSelect?: boolean
  }

  const props = defineProps<Props>()

  const referenceEl = useTemplateRef<HTMLElement>('menu-button')
  const floatingEl = useTemplateRef<HTMLElement>('floatingEl')

  const { floatingStyles } = useFloating(referenceEl, floatingEl, {
    strategy: 'fixed',
    placement: computed(() => (props.displayAsChild ? 'right-start' : 'bottom-start')),
    middleware: computed(() => {
      if (props.displayAsChild) {
        return [
          flip({
            fallbackPlacements: ['left-start'],
          }),
        ]
      }
      return [
        flip({
          // Preserve previous behavior that kept some viewport breathing room.
          padding: 50,
          fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
        }),
      ]
    }),
    whileElementsMounted: autoUpdate,
  })

  const isMenuItemGroup = (item: SingleMenuItem | MenuItemGroup): item is MenuItemGroup =>
    !('id' in item)

  const isGrouped = computed(() => props.items.some((item) => isMenuItemGroup(item)))

  const groupedItems = computed(() => (isGrouped.value ? (props.items as MenuItemGroup[]) : []))

  const ungroupedItems = computed(() => (isGrouped.value ? [] : (props.items as SingleMenuItem[])))

  const flattenedItems = computed(() =>
    isGrouped.value ? groupedItems.value.flatMap((group) => group.items) : ungroupedItems.value,
  )

  const hasItemWithTrailingContent = computed(() => {
    return flattenedItems.value.some((item) => item.trailingIcon || item.items || item.trailingText)
  })

  const isMouseWithin = ref(false)

  const hasLeadingIcons = computed(() =>
    flattenedItems.value.some((item) => item.leadingIcon != undefined),
  )
</script>
